// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Orchestration: a pool of decode workers feeding one inference worker.
 *
 * The two phases must not share a thread — see decode.worker.ts for the 7.6x measurement that
 * forces it — but they should overlap, because decoding a 12-megapixel photograph takes about
 * 180 ms and embedding it about 140 ms. Run in parallel, the wall clock is the slower of the two
 * rather than their sum.
 *
 * The main thread is the pipe. That sounds wrong for a tool whose whole premise is keeping the UI
 * responsive, but everything crossing it is a transferable ArrayBuffer of 256 KB, and the only
 * work it does per image is bookkeeping. What it buys is backpressure: decode workers are handed a
 * new file only when the inference worker has taken the last one, so the queue of decoded frames
 * cannot grow past the pool size however much faster decoding turns out to be.
 */

import type { ImageRecord } from './core/types.ts';
import { EmbeddingMatrix } from './core/vectors.ts';
import type { DecodeRequest, DecodeResponse } from './workers/decode.worker.ts';
import type { EmbedRequest, EmbedResponse } from './workers/embed.worker.ts';
import type { BackendReport } from './model/embedder.ts';
import { EMBED_DIM } from './model/config.mjs';

export interface RunOptions {
  readonly paths: readonly string[];
  readonly files: readonly File[];
  readonly useEmbeddings: boolean;
  readonly ortBase: string;
  onLog(level: 'info' | 'good' | 'warn' | 'error', message: string): void;
  onModelProgress(loaded: number, total: number): void;
  onBackend(report: BackendReport): void;
  onProgress(done: number, total: number, rate: number): void;
}

export interface RunResult {
  readonly records: ImageRecord[];
  readonly matrix: EmbeddingMatrix | null;
  readonly failed: { path: string; reason: string }[];
  readonly elapsedMs: number;
  readonly cancelled: boolean;
}

/** A rolling rate, so the estimate tracks thermal throttling instead of averaging it away. */
class Rate {
  private readonly times: number[] = [];
  mark(now: number): number {
    this.times.push(now);
    if (this.times.length > 24) this.times.shift();
    if (this.times.length < 4) return -1;
    const span = this.times[this.times.length - 1]! - this.times[0]!;
    return span > 0 ? ((this.times.length - 1) / span) * 1000 : -1;
  }
}

export class Pipeline {
  private decoders: Worker[] = [];
  private embedWorker: Worker | null = null;
  private cancelled = false;

  cancel(): void {
    this.cancelled = true;
  }

  terminate(): void {
    for (const w of this.decoders) w.terminate();
    this.decoders = [];
    this.embedWorker?.terminate();
    this.embedWorker = null;
  }

  async run(opts: RunOptions): Promise<RunResult> {
    this.cancelled = false;
    const started = performance.now();
    const total = opts.files.length;
    const records: ImageRecord[] = [];
    const failed: { path: string; reason: string }[] = [];
    // Vectors are collected against their image id, NOT appended to the matrix as they arrive.
    // Results come back out of dispatch order, and row `i` of the matrix must be `records[i]` —
    // appending in completion order and then sorting the records would silently pair every
    // photograph with somebody else's embedding.
    const vectors = opts.useEmbeddings ? new Map<number, Float32Array>() : null;

    // Two decoders is the sweet spot rather than hardwareConcurrency: decode is only ~1.3x the
    // cost of inference, so a third decoder would sit idle while multiplying peak memory — and
    // peak memory during decode is the likeliest way to have a phone discard the tab.
    const poolSize = Math.max(1, Math.min(2, (navigator.hardwareConcurrency || 2) - 1));
    for (let i = 0; i < poolSize; i++) {
      this.decoders.push(new Worker(new URL('./workers/decode.worker.ts', import.meta.url), { type: 'module' }));
    }

    if (vectors) {
      this.embedWorker = new Worker(new URL('./workers/embed.worker.ts', import.meta.url), { type: 'module' });
      await this.loadModel(this.embedWorker, opts);
      if (this.cancelled) {
        this.terminate();
        return { records, matrix: null, failed, elapsedMs: performance.now() - started, cancelled: true };
      }
    }

    const rate = new Rate();
    let next = 0;
    let done = 0;
    let nextId = 0;

    await new Promise<void>((resolve, reject) => {
      const finishOne = (): void => {
        done++;
        opts.onProgress(done, total, rate.mark(performance.now()));
        if (done >= total || this.cancelled) resolve();
      };

      const embedQueue: { record: ImageRecord; rgba: ArrayBuffer }[] = [];
      let embedBusy = false;

      const pumpEmbed = (): void => {
        if (embedBusy || embedQueue.length === 0 || !this.embedWorker) return;
        embedBusy = true;
        const job = embedQueue[0]!;
        const msg: EmbedRequest = { kind: 'embed', id: job.record.id, rgba: job.rgba };
        this.embedWorker.postMessage(msg, [job.rgba]);
      };

      if (this.embedWorker) {
        this.embedWorker.onmessage = (e: MessageEvent<EmbedResponse>): void => {
          const m = e.data;
          if (m.kind === 'log') return opts.onLog(m.level, m.message);
          if (m.kind === 'error') return reject(new Error(m.message));
          if (m.kind !== 'vector') return;
          const job = embedQueue.shift();
          embedBusy = false;
          if (job) {
            vectors!.set(job.record.id, new Float32Array(m.vector));
            records.push(job.record);
            finishOne();
          }
          pumpEmbed();
        };
      }

      const feed = (w: Worker): void => {
        if (this.cancelled || next >= total) return;
        const i = next++;
        const req: DecodeRequest = {
          id: nextId++,
          path: opts.paths[i]!,
          file: opts.files[i]!,
          wantPixels: vectors !== null,
        };
        w.postMessage(req);
      };

      for (const w of this.decoders) {
        w.onmessage = (e: MessageEvent<DecodeResponse>): void => {
          const m = e.data;
          if (m.kind === 'fail') {
            failed.push({ path: m.path, reason: m.reason });
            opts.onLog('warn', `Could not read ${m.path} — skipped.`);
            finishOne();
          } else if (m.rgba && vectors) {
            embedQueue.push({ record: m.record, rgba: m.rgba });
            pumpEmbed();
          } else {
            records.push(m.record);
            finishOne();
          }
          feed(w);
        };
        w.onerror = (ev) => reject(new Error(`A decode worker failed: ${ev.message}`));
        feed(w);
      }

      if (total === 0) resolve();
    });

    this.terminate();

    // Ids are assigned in dispatch order but results arrive out of order, so renumber to the
    // final array positions — every downstream structure (the embedding matrix rows, the report,
    // the export) indexes by `id`, and a gap or a reorder would silently mismatch a vector to a
    // photograph.
    records.sort((a, b) => a.id - b.id);
    const renumbered = records.map((r, i) => ({ ...r, id: i }));

    // Built here, in final record order. `push` L2-normalises each row, which is required rather
    // than tidy: the model's `image_embeds` is NOT unit length (measured norms of 0.98 and 1.14 on
    // real photographs), so a bare dot product on the raw output is not a cosine and ranks wrongly.
    let matrix: EmbeddingMatrix | null = null;
    if (vectors) {
      matrix = new EmbeddingMatrix(EMBED_DIM, renumbered.length);
      for (const r of records) matrix.push(vectors.get(r.id)!);
    }

    return {
      records: renumbered,
      matrix,
      failed,
      elapsedMs: performance.now() - started,
      cancelled: this.cancelled,
    };
  }

  private loadModel(w: Worker, opts: RunOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      w.onmessage = (e: MessageEvent<EmbedResponse>): void => {
        const m = e.data;
        if (m.kind === 'progress') opts.onModelProgress(m.loaded, m.total);
        else if (m.kind === 'log') opts.onLog(m.level, m.message);
        else if (m.kind === 'ready') {
          opts.onBackend(m.report);
          resolve();
        } else if (m.kind === 'error') reject(new Error(m.message));
      };
      w.onerror = (ev) => reject(new Error(`The model worker failed: ${ev.message}`));
      const msg: EmbedRequest = { kind: 'load', ortBase: opts.ortBase };
      w.postMessage(msg);
    });
  }
}
